/* eslint-disable @next/next/no-img-element */
import Image from 'next/image';
import { unit, Canvas, Abs, Avatar, BLogo, Icon } from './kit';

// Vector rebuild of /img/redesign/hero-primary-v5.png (1451×1084 canvas).
// The Slack window is HTML; the 3D people and props are a transparent PNG
// layer (hero-people-v2.png, cut from the original render) placed on top.
const W = 1451;
const H = 1084;
const u = unit(W);

const INK = '#1F1B24';
const TEXT = '#2B2730';
const MUTE = '#7C7882';
const SIDEBAR = '#1B102A';
const PANEL = '#FCFAF8';

// The Slack window is drawn at the original render's coordinates, then
// enlarged around its bottom-left corner and nudged left (behind the man) so
// it stays on screen, while the 3D characters keep their size.
const WINDOW_SCALE = 1.15;
const WINDOW_ORIGIN = [531, 949];
const WINDOW_SHIFT = -130;

// People are shown smaller than in the render, each group anchored to the
// ground at its outer bottom corner.
const PEOPLE_SCALE = 0.85;
const PEOPLE_GROUPS = [
  { clip: 'inset(0 50% 0 0)', origin: [60, 1060] },
  { clip: 'inset(0 0 0 50%)', origin: [1440, 1076] },
];

const T = ({ x, y, size, weight = 500, color = TEXT, style, children }) => (
  <Abs u={u} x={x} y={y - size * 0.62} style={{ fontSize: u(size), fontWeight: weight, color, lineHeight: 1.25, ...style }}>
    {children}
  </Abs>
);

const Svg = ({ size, color, children, stroke = 2 }) => (
  <svg viewBox="0 0 24 24" style={{ width: u(size), height: u(size), display: 'block', color }} fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const Square = ({ name, x, y, size = 36 }) => (
  <Abs u={u} x={x} y={y} w={size} h={size}>
    <img src={`/img/redesign/avatars/${name}.webp`} alt="" draggable="false" style={{ width: '100%', height: '100%', display: 'block', borderRadius: u(8) }} />
  </Abs>
);

const Header = ({ y, avatar, name, time, app }) => (
  <>
    <Square name={avatar} x={664} y={y - 10} />
    <T x={707} y={y} size={15} weight={700} color={INK}>{name}</T>
    {app && (
      <Abs u={u} x={768} y={y - 7.5} h={15} style={{
        display: 'flex', alignItems: 'center', padding: `0 ${u(5)}`, borderRadius: u(4),
        background: '#ECEAEF', color: '#5E5A66', fontSize: u(9), fontWeight: 700, letterSpacing: '0.02em',
      }}>APP</Abs>
    )}
    <T x={app ? 803 : name.length > 8 ? 793 : 790} y={y + 0.5} size={12.5} color={MUTE}>{time}</T>
  </>
);

// Reaction pills tucked into a card's bottom-right corner (no extra height).
const CardReactions = ({ right, y, items }) => (
  <Abs u={u} x={right - 320} y={y - 12} w={320} h={24} style={{ display: 'flex', justifyContent: 'flex-end', gap: u(6) }}>
    {items.map(([emoji, count]) => (
      <span key={emoji} style={{
        display: 'flex', alignItems: 'center', gap: u(5), height: u(24), padding: `0 ${u(8)}`, boxSizing: 'border-box',
        background: 'rgba(255, 255, 255, 0.78)', border: `${u(1)} solid rgba(31, 27, 36, 0.08)`, borderRadius: u(12),
        fontSize: u(12.5), fontWeight: 600, color: TEXT,
      }}>
        <span style={{ fontSize: u(13) }}>{emoji}</span>{count}
      </span>
    ))}
  </Abs>
);

const POLL = [
  { emoji: '😀', label: 'Great', pct: 62, share: 58 },
  { emoji: '😐', label: 'Fine', pct: 28, share: 30 },
  { emoji: '😕', label: 'Rough', pct: 10, share: 11 },
];

// Message cards span the channel; the stack is scaled up by MESSAGES_SCALE
// from its top-left so it reaches the panel's right edge.
const MESSAGES_SCALE = 1.22;
const CARD_W = 460;
const POLL_BAR_W = CARD_W - 26 - 97 - 56;

export default function HeroVisual({ className, sizes, imageStyle = {} }) {
  const { filter, ...peopleImageStyle } = imageStyle;

  return (
    <Canvas W={W} H={H} className={className} label="BuddiesHR, kudos, pulse and birthdays running inside a Slack channel, surrounded by friendly mascots">
      <div style={{
        position: 'absolute', inset: 0,
        transform: `translateX(${u(WINDOW_SHIFT)}) scale(${WINDOW_SCALE})`,
        transformOrigin: `${u(WINDOW_ORIGIN[0])} ${u(WINDOW_ORIGIN[1])}`,
      }}>
        {/* Window frame */}
        <Abs u={u} x={531} y={119} w={787} h={830} style={{
          background: '#F8F0E9', borderRadius: u(30), border: `${u(1.5)} solid #EFE5DC`, boxSizing: 'border-box',
          boxShadow: `0 ${u(24)} ${u(60)} rgba(31, 27, 22, 0.12), 0 ${u(4)} ${u(12)} rgba(31, 27, 22, 0.05)`,
        }} />
        {[['#F25F58', 560], ['#F8BC2E', 577], ['#3BC94A', 594]].map(([c, x]) => (
          <Abs key={c} u={u} x={x - 5.5} y={145.5} w={11} h={11} style={{ borderRadius: '50%', background: c }} />
        ))}

        {/* Sidebar */}
        <Abs u={u} x={540} y={170} w={97} h={690} style={{ background: SIDEBAR, borderRadius: u(22) }} />
        <Abs u={u} x={555} y={188}><BLogo u={u} size={62} /></Abs>
        {[['home', 300], ['chat', 363], ['users', 498], ['chart', 562], ['gear', 625]].map(([name, y]) => (
          <Abs key={name} u={u} x={570} y={y - 15} style={{ color: '#fff' }}>
            <Icon u={u} name={name} size={30} stroke={name === 'chat' ? 2.4 : 2.2} />
          </Abs>
        ))}
        <Abs u={u} x={550} y={405} w={72} h={52} style={{
          background: '#7B4DFF', borderRadius: u(10), color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon u={u} name="hash" size={32} stroke={2.6} />
        </Abs>
        <Abs u={u} x={556} y={755}><Avatar u={u} name="hero-me" size={54} dot={SIDEBAR} /></Abs>

        {/* Channel panel */}
        <Abs u={u} x={641} y={160} w={657} h={782} style={{ background: PANEL, borderRadius: `${u(16)} ${u(16)} ${u(4)} ${u(4)}` }} />
        <T x={668} y={195} size={19} weight={700} color={INK}># kudos 💪</T>
        {[938, 958, 978, 998].map((cx, i) => (
          <Abs key={cx} u={u} x={cx - 12} y={180}>
            <Avatar u={u} name={`hero-stack${i + 1}`} size={24} ring={2} />
          </Abs>
        ))}
        <Abs u={u} x={1008} y={181} w={33} h={22} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: u(11),
          background: '#EFECEF', fontSize: u(12), fontWeight: 600, color: '#4A4652',
        }}>+12</Abs>
        <Abs u={u} x={1187} y={180}>
          <Svg size={20} color="#4A4652"><path d="M4 15v-3a8 8 0 0 1 16 0v3" /><rect x="3" y="14" width="4.5" height="6.5" rx="1.6" /><rect x="16.5" y="14" width="4.5" height="6.5" rx="1.6" /></Svg>
        </Abs>
        <Abs u={u} x={1222} y={180}>
          <Svg size={20} color="#4A4652"><path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 8 2.5 8h-17S6 15 6 9z" /><path d="M10 20.5a2.2 2.2 0 0 0 4 0" /></Svg>
        </Abs>
        <Abs u={u} x={1256} y={180}>
          <Svg size={20} color="#4A4652" stroke={3}><path d="M5 12h.01M12 12h.01M19 12h.01" /></Svg>
        </Abs>
        <Abs u={u} x={641} y={218} w={657} h={1.2} style={{ background: '#EFEAE5' }} />

        {/* Messages: laid out in the render's coordinates, then enlarged so
            they fill the channel width and stay legible. */}
        <div style={{
          position: 'absolute', inset: 0,
          transform: `scale(${MESSAGES_SCALE})`,
          transformOrigin: `${u(664)} ${u(230)}`,
        }}>
          {/* Maya: kudos */}
          <Header y={248} avatar="hero-maya" name="Maya K." time="10:42 AM" app />
          <T x={707} y={269} size={15} color={TEXT}>
            Kudos to <span style={{ background: '#E4EEFC', color: '#2F63C8', borderRadius: u(4), padding: `0 ${u(2)}` }}>@Jordan Lee</span> for shipping the new onboarding flow!
          </T>
          <Abs u={u} x={706} y={288} w={CARD_W} h={122} style={{
            background: '#E8F4E3', borderRadius: u(6), borderLeft: `${u(4)} solid #4FA35A`, boxSizing: 'border-box',
          }} />
          <T x={727} y={310} size={14.5} weight={700} color={INK}>Shipped the new onboarding flow 🎉</T>
          <T x={727} y={338} size={13.5} color="#3A3640">“Jordan stayed late last week debugging the SSO bug —</T>
          <T x={727} y={361} size={13.5} color="#3A3640">saved our launch. Living the #craft value.”</T>
          <T x={727} y={391} size={11.5} weight={600} color="#3A3640" style={{ letterSpacing: '0.04em' }}>+120 POINTS&nbsp;&nbsp;•&nbsp;&nbsp;#CRAFT</T>
          <CardReactions right={706 + CARD_W - 12} y={389} items={[['🎉', 12], ['❤️', 8], ['🔥', 3]]} />

          {/* Jordan: pulse poll */}
          <Header y={434} avatar="hero-jordan" name="Jordan Lee" time="10:44 AM" />
          <Abs u={u} x={706} y={456} w={CARD_W} h={126} style={{ background: '#E3ECFB', borderRadius: u(8) }} />
          <T x={719} y={474} size={14.5} weight={700} color={INK}>How’s this week going?</T>
          {POLL.map((p, i) => {
            const y = 499 + i * 23;
            return (
              <Abs key={p.label} u={u} x={719} y={y - 9} w={CARD_W - 26} h={18}>
                <Abs u={u} x={0} y={-1} style={{ fontSize: u(14) }}>{p.emoji}</Abs>
                <Abs u={u} x={21} y={0} style={{ fontSize: u(13.5), color: TEXT }}>{p.label}</Abs>
                <Abs u={u} x={97} y={5} w={POLL_BAR_W} h={8} style={{ background: '#CFDCF5', borderRadius: u(4) }}>
                  <div style={{ width: `${p.share}%`, height: '100%', borderRadius: u(4), background: '#5A8DEB' }} />
                </Abs>
                <Abs u={u} x={CARD_W - 26 - 48} y={0} w={44} style={{ textAlign: 'right', fontSize: u(13), color: TEXT }}>{p.pct}%</Abs>
              </Abs>
            );
          })}
          <T x={719} y={568} size={11.5} weight={600} color="#3A3640" style={{ letterSpacing: '0.04em' }}>42 OF 58 RESPONDED</T>
          <CardReactions right={706 + CARD_W - 12} y={566} items={[['👍', 9], ['🙌', 5]]} />

          {/* Priya: birthday */}
          <Header y={604} avatar="hero-priya" name="Priya Shah" time="10:47 AM" />
          <T x={707} y={626} size={15} color={TEXT}>It’s Priya’s birthday today!</T>
          <Abs u={u} x={706} y={647} w={CARD_W} h={98} style={{ background: '#EDE3FB', borderRadius: u(8) }} />
          <T x={721} y={664} size={14.5} weight={700} color={INK}>Wish Priya a happy birthday 🎂</T>
          <T x={721} y={693} size={13} color="#3A3640">Priya has been part of the team for 2 years.</T>
          <T x={721} y={717} size={13} color="#3A3640">Drop a message!</T>
          <CardReactions right={706 + CARD_W - 12} y={719} items={[['🎂', 24], ['🎉', 18], ['💜', 11]]} />
        </div>

        {/* Composer */}
        <Abs u={u} x={713} y={878} w={424} h={47} style={{
          background: '#FFFFFF', border: `${u(1.5)} solid #E8E1DB`, borderRadius: u(12), boxSizing: 'border-box',
        }} />
        <Abs u={u} x={722} y={892}><Icon u={u} name="plus" size={18} color="#6E6A75" stroke={2.2} /></Abs>
        <T x={749} y={902} size={13} color="#8C8892">Message #kudos</T>
        <Abs u={u} x={1106} y={893}>
          <Svg size={19} color="#B9B4BE"><path d="M4 12 20 4l-4 16-4-7z" fill="currentColor" stroke="none" /></Svg>
        </Abs>

        {/* Floating action buttons */}
        <Abs u={u} x={1272} y={253} w={88} h={82} style={{
          background: 'linear-gradient(160deg, #F1EFFF 0%, #DCD8FA 100%)', borderRadius: u(20),
          boxShadow: `0 ${u(10)} ${u(24)} rgba(60, 40, 140, 0.16)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: u(44),
        }}>🎉</Abs>
        <Abs u={u} x={1278} y={370} w={80} h={80} style={{
          background: 'radial-gradient(circle at 35% 30%, #9A7BFA 0%, #6B4BDC 70%)', borderRadius: '50%',
          boxShadow: `0 ${u(10)} ${u(24)} rgba(80, 50, 180, 0.30)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
        }}>
          <Svg size={38} color="#fff" stroke={3.4}><path d="M6 19v-5M12 19V9M18 19V5" /></Svg>
        </Abs>
        <Abs u={u} x={1271} y={471} w={92} h={92} style={{
          background: '#FFFFFF', borderRadius: '50%',
          boxShadow: `0 ${u(10)} ${u(24)} rgba(40, 30, 80, 0.14)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon u={u} name="smile" size={50} color="#3E9E4E" stroke={2.4} />
        </Abs>
      </div>

      {/* Decorative star (redrawn: the render's 3D star now overlaps the window) */}
      <Abs u={u} x={318} y={300} w={52} h={52}>
        <svg viewBox="0 0 24 24" style={{ width: '100%', height: '100%', display: 'block', filter: `drop-shadow(0 ${u(4)} ${u(6)} rgba(214, 140, 20, 0.35))` }} aria-hidden="true">
          <defs>
            <linearGradient id="hero-star" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#FFD45C" />
              <stop offset="1" stopColor="#F5A623" />
            </linearGradient>
          </defs>
          <path d="M12 2.6l2.9 5.9 6.5.9-4.7 4.6 1.1 6.4L12 17.4l-5.8 3 1.1-6.4-4.7-4.6 6.5-.9z" fill="url(#hero-star)" stroke="#F5A623" strokeWidth="1.4" strokeLinejoin="round" />
        </svg>
      </Abs>

      {/* 3D people and props, cut out of the original render. The left and
          right groups are shown from the same image, each clipped to its half
          and scaled down towards its own bottom corner. The filter stays on
          the outer wrapper: a drop-shadow inside the clip gets cut into a
          visible hard-edged box. */}
      {PEOPLE_GROUPS.map(g => (
        <div key={g.clip} style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          filter,
          transform: `scale(${PEOPLE_SCALE})`,
          transformOrigin: `${u(g.origin[0])} ${u(g.origin[1])}`,
        }}>
          <div style={{ position: 'absolute', inset: 0, clipPath: g.clip }}>
            <Image
              src="/img/redesign/hero-people-v2.png"
              alt=""
              fill
              priority
              sizes={sizes}
              draggable={false}
              style={{ objectFit: 'contain', ...peopleImageStyle }}
            />
          </div>
        </div>
      ))}
    </Canvas>
  );
}
